import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Avatar, InputAdornment, Card, CardContent, Grid, Divider, useMediaQuery } from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import Fade from '@mui/material/Fade';

// MOCK clubs (à remplacer par un fetch réel plus tard)
const clubsMock = [
    {
        id: 101,
        nom: 'Alianza Lima',
        sport: 'Football',
        horaires: 'Mon-Sun 8am-8pm',
        adresse: 'Estadio Nacional, Lima',
        ville: 'Lima',
        telephone: '+51 987 654 321',
        email: 'contact@alianzalima.pe',
        description: 'Historic football club from Lima.',
        logo: '',
        facebook: '',
        instagram: '',
        site: '',
    },
    {
        id: 102,
        nom: 'Lima Basketball Club',
        sport: 'Basketball',
        horaires: 'Mon-Sat 10am-10pm',
        adresse: 'Coliseo Eduardo Dibós, Lima',
        ville: 'Lima',
        telephone: '+51 912 345 678',
        email: 'info@limabasket.pe',
        description: 'Basketball club from the capital.',
        logo: '',
        facebook: '',
        instagram: '',
        site: '',
    },
];

// MOCK événements (extrait de Events.js) avec clubId
const allEvents = [
    {
        id: 1,
        title: 'Football Match - Liga 1',
        date: 'December 15, 2024',
        city: 'Lima',
        clubId: 101,
    },
    {
        id: 2,
        title: 'Basketball Championship',
        date: 'December 20, 2024',
        city: 'Lima',
        clubId: 102,
    },
    {
        id: 3,
        title: 'Volleyball Tournament',
        date: 'December 25, 2024',
        city: 'Lima',
        clubId: null,
    },
    {
        id: 4,
        title: 'Cusco Tennis Open',
        date: 'January 10, 2025',
        city: 'Cusco',
        clubId: null,
    },
];

const ClubConfig = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const club = clubsMock.find((c) => c.id === Number(id));
    const isMobile = useMediaQuery('(max-width:900px)');

    // États pour chaque champ
    const [nom, setNom] = useState(club?.nom || '');
    const [sport, setSport] = useState(club?.sport || '');
    const [horaires, setHoraires] = useState(club?.horaires || '');
    const [adresse, setAdresse] = useState(club?.adresse || '');
    const [ville, setVille] = useState(club?.ville || '');
    const [telephone, setTelephone] = useState(club?.telephone || '');
    const [email, setEmail] = useState(club?.email || '');
    const [description, setDescription] = useState(club?.description || '');
    const [logo, setLogo] = useState(club?.logo || '');
    const [facebook, setFacebook] = useState(club?.facebook || '');
    const [instagram, setInstagram] = useState(club?.instagram || '');
    const [site, setSite] = useState(club?.site || '');
    const [saved, setSaved] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    if (!club) {
        return <Typography color="error">Club not found.</Typography>;
    }

    const handleSave = () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    // Gestion upload logo (mock)
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setLogo(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    // Fonction utilitaire pour marquer le formulaire comme modifié
    const handleChange = (setter) => (e) => {
        setter(e.target.value);
        setIsDirty(true);
    };

    return (
        <Box sx={{
            width: '100%',
            maxWidth: 1200,
            minHeight: 'calc(100vh - 64px)',
            background: 'white',
            p: 3,
            borderRadius: 0,
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            overflowX: 'auto',
            margin: '0 auto',
        }}>
            {/* Bouton retour */}
            <Button variant="outlined" onClick={() => navigate('/admin-panel')} sx={{ mb: 1, borderRadius: 2, fontWeight: 600, textTransform: 'none', px: 2, alignSelf: 'flex-start' }}>
                Back
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#E53E3E', mb: 2, textAlign: 'left' }}>
                Club Configuration
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 3, width: '100%', alignItems: 'stretch', overflowX: 'auto', maxWidth: '100%', minWidth: 0 }}>
                {/* Colonne de gauche */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, height: '100%', minWidth: 0 }}>
                    {/* Identité */}
                    <Card sx={{ background: 'white', borderRadius: 4, boxShadow: '0 4px 24px rgba(255,107,107,0.10)', p: 4, height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar src={logo} sx={{ width: 60, height: 60, mr: 2, bgcolor: '#FF6B6B', fontSize: 26 }}>
                                    {(!logo && nom) ? nom.charAt(0) : ''}
                                </Avatar>
                                <Button component="label" variant="outlined" color="error" startIcon={<PhotoCamera />} sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', fontSize: 13, height: 38 }}>
                                    Change logo
                                    <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                                </Button>
                            </Box>
                            <TextField label="Club name" value={nom} onChange={handleChange(setNom)} fullWidth InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2 }} />
                            <TextField label="Sport" value={sport} onChange={handleChange(setSport)} fullWidth InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2 }} />
                            <TextField label="Description" value={description} onChange={handleChange(setDescription)} fullWidth multiline minRows={2} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" />
                        </CardContent>
                    </Card>
                    {/* Coordonnées */}
                    <Card sx={{ background: 'white', borderRadius: 4, boxShadow: '0 4px 24px rgba(255,107,107,0.10)', p: 4, height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <LocationOnIcon sx={{ color: '#E53E3E', mr: 1 }} />
                                <Typography fontWeight={600}>Coordinates</Typography>
                            </Box>
                            <TextField label="Address" value={adresse} onChange={handleChange(setAdresse)} fullWidth InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2 }} />
                            <TextField label="City" value={ville} onChange={handleChange(setVille)} fullWidth InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2 }} />
                            <TextField label="Opening hours" value={horaires} onChange={handleChange(setHoraires)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon sx={{ color: '#E53E3E' }} /></InputAdornment> }} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" />
                        </CardContent>
                    </Card>
                    {/* Événements organisés par le club */}
                    <Card sx={{ background: 'white', borderRadius: 4, boxShadow: '0 4px 24px rgba(255,107,107,0.10)', p: 4, mt: 1 }}>
                        <CardContent sx={{ p: 0 }}>
                            <Typography fontWeight={600} sx={{ color: '#E53E3E', mb: 1 }}>Organized events</Typography>
                            {allEvents.filter(ev => ev.clubId === club.id).length === 0 ? (
                                <Typography color="text.secondary" fontSize={15}>No event organized</Typography>
                            ) : (
                                allEvents.filter(ev => ev.clubId === club.id).map(ev => (
                                    <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', mb: 1.2 }}>
                                        <Typography fontWeight={500} sx={{ flex: 1 }}>{ev.title}</Typography>
                                        <Typography color="text.secondary" fontSize={14} sx={{ mr: 2 }}>{ev.date}</Typography>
                                        <Button size="small" component={Link} to={`/event/${ev.id}`} variant="outlined" sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', px: 2 }}>View</Button>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Box>
                {/* Colonne de droite */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, height: '100%', minWidth: 0 }}>
                    {/* Contact */}
                    <Card sx={{ background: 'white', borderRadius: 4, boxShadow: '0 4px 24px rgba(255,107,107,0.10)', p: 4, height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PhoneIcon sx={{ color: '#E53E3E', mr: 1 }} />
                                <Typography fontWeight={600}>Contact</Typography>
                            </Box>
                            <TextField label="Phone" value={telephone} onChange={handleChange(setTelephone)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#E53E3E' }} /></InputAdornment> }} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2, maxWidth: '100%' }} />
                            <TextField label="Contact email" value={email} onChange={handleChange(setEmail)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#E53E3E' }} /></InputAdornment> }} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ maxWidth: '100%' }} />
                        </CardContent>
                    </Card>
                    {/* Réseaux sociaux */}
                    <Card sx={{ background: 'white', borderRadius: 4, boxShadow: '0 4px 24px rgba(255,107,107,0.10)', p: 4, height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoIcon sx={{ color: '#E53E3E', mr: 1 }} />
                                <Typography fontWeight={600}>Social networks</Typography>
                            </Box>
                            <TextField label="Facebook" value={facebook} onChange={handleChange(setFacebook)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{ color: '#4267B2' }} /></InputAdornment> }} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2, maxWidth: '100%' }} />
                            <TextField label="Instagram" value={instagram} onChange={handleChange(setInstagram)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon sx={{ color: '#E1306C' }} /></InputAdornment> }} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ mb: 2, maxWidth: '100%' }} />
                            <TextField label="Site web" value={site} onChange={handleChange(setSite)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><LanguageIcon sx={{ color: '#E53E3E' }} /></InputAdornment> }} InputLabelProps={{ style: { color: '#E53E3E' } }} size="small" sx={{ maxWidth: '100%' }} />
                        </CardContent>
                    </Card>
                </Box>
            </Box>
            <Divider sx={{ my: 3 }} />
            {/* Bouton enregistrer centré, visible seulement si modifié, avec animation */}
            <Fade in={isDirty} timeout={400} unmountOnExit>
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => { handleSave(); setIsDirty(false); }}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 5,
                            py: 1.5,
                            fontSize: 18,
                            background: '#FF6B6B',
                            color: 'white',
                            boxShadow: '0 4px 24px rgba(229,62,62,0.12)',
                            transition: 'background 0.2s, box-shadow 0.2s, transform 0.2s',
                            '&:hover': {
                                background: '#E53E3E',
                                boxShadow: '0 6px 32px rgba(229,62,62,0.18)',
                                transform: 'translateY(-2px) scale(1.04)',
                            },
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Fade>
            {saved && (
                <Typography color="success.main" sx={{ mt: 2, fontWeight: 600, textAlign: 'center', width: '100%' }}>
                    Changes saved!
                </Typography>
            )}
        </Box>
    );
};

export default ClubConfig; 